import { useEffect, useRef, type MouseEvent, type PointerEvent } from 'react';

/** Held this long (ms), a card opens its menu instead of being tapped. */
const LONG_PRESS = 500;

/**
 * Press and hold – first on shopping tiles (Mike, 15.10.: "Sollten Änderungen wie Menge nicht auch
 * durch längeres Gedrückthalten gehen?"), since 1.28 on job cards. A finger that moves (scrolling)
 * cancels it; a visible button stays the way for keyboards and screen readers.
 */
export function useLongPress(onLongPress?: () => void) {
  const timer = useRef(0);
  const fired = useRef(false);
  const start = useRef({ x: 0, y: 0 });
  const cancel = () => window.clearTimeout(timer.current);
  useEffect(() => cancel, []);
  if (!onLongPress) return { handlers: {}, consume: () => false };
  return {
    handlers: {
      onPointerDownCapture: (e: PointerEvent) => {
        fired.current = false;
        start.current = { x: e.clientX, y: e.clientY };
        cancel();
        timer.current = window.setTimeout(() => {
          fired.current = true;
          navigator.vibrate?.(15);
          onLongPress();
        }, LONG_PRESS);
      },
      onPointerMoveCapture: (e: PointerEvent) => {
        const d = Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y);
        if (d > 8) cancel();
      },
      onPointerUpCapture: cancel,
      onPointerCancelCapture: cancel,
      // no browser menu or link preview on a long press
      onContextMenu: (e: MouseEvent) => e.preventDefault(),
    },
    /** The press that ends a long press must not also tap the item. */
    consume: () => {
      const was = fired.current;
      fired.current = false;
      return was;
    },
  };
}
